import * as React from 'react'
import { observer } from 'mobx-react'
import Button from 'components/Button'
import { BalanceStatus } from 'jobs/Job'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetHeader,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
} from 'components/ModalSheet'
import JobViewModel from '../JobViewModel'
import { match } from 'utils/pattern-match-util'
import { SettleBalanceState } from './SettleBalanceViewModel'

interface SettleBalanceDialogProps {
  vm: JobViewModel
}

/**
 * The SettleBalanceDialog
 */
export default observer(function SettleBalanceDialog({
  vm: {
    job: { balanceStatus },
    settleBalanceVM: vm,
  },
}: SettleBalanceDialogProps) {
  const disabled = vm.settle.pending
  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.close}
      disableCloseOnOutsideClick={disabled}
      disableCloseOnEsc={disabled}
    >
      <ModalSheetHeader withCloseButton />
      {match(vm.state, {
        [SettleBalanceState.AwaitingConfirmation]: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <ModalSheetContentTitle>Settle Balance</ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  {balanceStatus === BalanceStatus.BalanceNegative ? (
                    <>The client has paid too much and is due a refund.</>
                  ) : (
                    <>The client has an outstanding balance.</>
                  )}{' '}
                  Are you ready to settle now?
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button disabled={disabled} onClick={vm.close}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  disabled={disabled}
                  loading={vm.settle.pending}
                  onClick={vm.settle}
                  dataTest="confirm-settle-balance-button"
                >
                  Settle
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [SettleBalanceState.Failed]: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <ModalSheetContentTitle>
                  Unable to Settle Balance
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  We were unable to settle the balance, it's possible that the
                  request currently is not in a state where balance settlement
                  is available, or it could be a transient error. Please try
                  again. If the issue persists, please contact an administrator.
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button disabled={disabled} onClick={vm.close}>
                  Close
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
        [SettleBalanceState.Success]: () => (
          <>
            <ModalSheetContent>
              <ModalSheetContentBlock>
                <ModalSheetContentTitle>
                  Balance Settlement has been Initiated.
                </ModalSheetContentTitle>
                <ModalSheetContentDescription>
                  If the client has already provided payment information, a
                  charge will be attempted shortly. You will be notified and the
                  result will appear in the Events section.
                </ModalSheetContentDescription>
              </ModalSheetContentBlock>
            </ModalSheetContent>
            <ModalSheetFooter>
              <ModalSheetFooterSpacer />
              <ModalSheetFooterButtonGroup>
                <Button onClick={vm.close}>Close</Button>
                <Button type="primary" onClick={vm.goToEvents}>
                  Go To Events
                </Button>
              </ModalSheetFooterButtonGroup>
            </ModalSheetFooter>
          </>
        ),
      })}
    </ModalSheet>
  )
})
